import { getValidToken } from '../utils/tokenUtils';
import { buildErrorFromResponse } from './apiHelpers';
import { fetchWithRateLimitRetry } from '../utils/fetchWithRateLimitRetry';

// Fetches metadata (type, description, placeholder) for all known testcase parameters
export const fetchParamMetadata = async () => {
  const token = await getValidToken();
  const res = await fetch('/api/test-configurations/param-metadata', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const metadata = await res.json();
  return Array.isArray(metadata) ? sortQuestionSections(metadata) : metadata;
};

// Fetches contract parameters for a specific organisation and contract.
// When sandbox=true, fetches the isolated sandbox configuration instead — the two are stored separately.
export const fetchContractParameters = async (organisationId, contractId, sandbox = false) => {
  const token = await getValidToken();
  const params = new URLSearchParams({ organisationId, contractId });
  if (sandbox) params.append('sandbox', 'true');
  const res = await fetch(`/api/test-configurations?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  const config = data[0];
  if (config.configurationJson && typeof config.configurationJson === 'string') {
    config.configurationJson = JSON.parse(config.configurationJson);
  }
  return config.configurationJson;
};

// Fetches the default contract parameters for a contract without reusing a tester's saved config
export const fetchDefaultContractParameters = async (contractId) => {
  const token = await getValidToken();
  const res = await fetch(`/api/test-configurations/default?contractId=${encodeURIComponent(contractId)}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  const config = data?.configurationJson ?? data;
  if (typeof config === 'string') {
    return JSON.parse(config);
  }
  return config;
};

// Saves contract parameters for a specific organisation and contract.
// When sandbox=true, saves to the isolated sandbox configuration instead of the standard one.
export const saveContractParameters = async (organisationId, contractId, parameters, sandbox = false) => {
  const token = await getValidToken();
  const params = new URLSearchParams({ organisationId, contractId });
  if (sandbox) params.append('sandbox', 'true');
  const res = await fetch(`/api/test-configurations?${params.toString()}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(parameters),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

// Fetches stored test results for a specific organisation and contract.
// When sandbox=true, fetches ONLY the isolated sandbox results instead — the two sets are never mixed.
export const fetchStoredResults = async (organisationId, contractId, sandbox = false) => {
  const token = await getValidToken();
  const params = new URLSearchParams({ organisationId, contractId });
  if (sandbox) params.append('sandbox', 'true');
  const res = await fetch(`/api/test-results?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return res.json();
};

// Posts a justification for a specific test result by id
export const saveJustification = async (testResultId, justification) => {
  const token = await getValidToken();
  const res = await fetch(`/api/test-results/${testResultId}/justification`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ justification }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => null);
    const err = new Error(`Failed to post justification: ${res.status}` + (text ? ` - ${text}` : ''));
    err.status = res.status;
    throw err;
  }

  return res.json();
};

// Adds a comment/note to a specific test result
export const saveTestResultComment = async (testResultId, commentText) => {
    const token = await getValidToken();
    const res = await fetch(`/api/test-results/${testResultId}/comments`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ commentText }),
    });

    if (!res.ok) {
        const text = await res.text().catch(() => null);
        const err = new Error(`Kunde inte spara kommentar (${res.status}).` + (text ? ` - ${text}` : ''));
        err.status = res.status;
        throw err;
    }

    return res.json();
};

// Edits an existing comment on a test result. Backend only allows this for the comment's own author.
export const updateTestResultComment = async (testResultId, commentId, commentText) => {
    const token = await getValidToken();
    const res = await fetch(`/api/test-results/${testResultId}/comments/${commentId}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ commentText }),
    });

    if (!res.ok) {
        const text = await res.text().catch(() => null);
        const err = new Error(`Kunde inte uppdatera kommentar (${res.status}).` + (text ? ` - ${text}` : ''));
        err.status = res.status;
        throw err;
    }

    return res.json();
};

// Removes a comment from a test result. Backend only allows this for the comment's own author.
export const deleteTestResultComment = async (testResultId, commentId) => {
    const token = await getValidToken();
    const res = await fetch(`/api/test-results/${testResultId}/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    if (!res.ok) {
        const text = await res.text().catch(() => null);
        const err = new Error(`Kunde inte ta bort kommentar (${res.status}).` + (text ? ` - ${text}` : ''));
        err.status = res.status;
        throw err;
    }

    return res.json();
};

// Fetches question sections for a specific contract
const sortQuestionSections = (sections = []) => {
  if (!Array.isArray(sections)) {
    return sections;
  }

  return [...sections]
    .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
    .map((section) => {
      if (!Array.isArray(section.questions)) {
        return { ...section };
      }

      return {
        ...section,
        questions: [...section.questions].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)),
      };
    });
};

export const fetchQuestions = async (contractId) => {
  const token = await getValidToken();
  const res = await fetch(`/api/contracts/${contractId}/question-sections`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => null);
    const err = new Error(`Kunde inte hämta frågor (${res.status}).` + (text ? ` - ${text}` : ''));
    err.status = res.status;
    throw err;
  }

  return sortQuestionSections(await res.json());
};

// Fetches stored answers for a specific section.
// Answers are owned per user. Omit ownerUserId to read your own; reviewers pass the
// submitting tester's Keycloak id to read the answers under review.
export const fetchQuestionAnswers = async (contractId, sectionId, organisationId, ownerUserId) => {
  const token = await getValidToken();
  const res = await fetch(
    `/api/contracts/${contractId}/question-sections/${sectionId}/answers` +
      `?organisationId=${encodeURIComponent(organisationId)}` +
      (ownerUserId ? `&ownerUserId=${encodeURIComponent(ownerUserId)}` : ''),
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!res.ok) {
    const text = await res.text().catch(() => null);
    const err = new Error(`Kunde inte hämta svar (${res.status}).` + (text ? ` - ${text}` : ''));
    err.status = res.status;
    throw err;
  }

  return res.json();
};

// Saves answers for a specific section.
// Omit ownerUserId to save your own answers; reviewers pass the submitting tester's
// Keycloak id to correct that tester's answers instead of creating their own record.
export const saveQuestionAnswers = async (contractId, sectionId, organisationId, answers, ownerUserId) => {
  const token = await getValidToken();
  const res = await fetchWithRateLimitRetry(
    `/api/contracts/${contractId}/question-sections/${sectionId}/answers?organisationId=${encodeURIComponent(organisationId)}` +
      (ownerUserId ? `&ownerUserId=${encodeURIComponent(ownerUserId)}` : ''),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(answers),
    }
  );

  if (!res.ok) {
    // Conflicts carry an AppErrorCode (MIMIR-3004) the caller needs in order to show
    // the conflict dialog, so parse the body properly instead of stringifying it.
    if (res.status === 409) {
      throw await buildErrorFromResponse(res);
    }
    const text = await res.text().catch(() => null);
    const err = new Error(`Kunde inte spara svar (${res.status}).` + (text ? ` - ${text}` : ''));
    err.status = res.status;
    throw err;
  }

  return res.json();
};

export const importQuestionAnswers = async (contractId, organisationId, file) => {
  const token = await getValidToken();
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(
    `/api/contracts/${encodeURIComponent(contractId)}/question-sections/import?organisationId=${encodeURIComponent(organisationId)}`,
    {
      method: 'POST',
      headers: { Authorization: ['Bearer', token].join(' ') },
      body: formData,
    }
  );

  if (!res.ok) {
    throw await buildErrorFromResponse(res);
  }
  return res.json();
};
